import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tooltip,
  InputAdornment,
} from '@mui/material';
import { CreateNewFolder, Warning as WarningIcon } from '@mui/icons-material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { useCompanyStore } from '@/stores/company-store';
import { useNavigationStore } from '@/stores/navigation-store';
import { useSnackbarStore } from '@certara/common-react-components';

interface CreateFolderProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function CreateFolder({ open: externalOpen, onOpenChange }: CreateFolderProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const [folderName, setFolderName] = useState('');
  const [error, setError] = useState('');
  const [warning, setWarning] = useState('');

  const isControlled = externalOpen !== undefined;
  const open = isControlled ? externalOpen : internalOpen;

  const { activeCompany } = useCompanyStore();
  const { currentPath } = useNavigationStore();
  const { addSnackbar } = useSnackbarStore();
  const queryClient = useQueryClient();

  interface CreateFolderResponse {
    IsSuccess: boolean;
    ErrorMessage?: string;
  }

  const createFolderMutation = useMutation({
    mutationFn: async () => {
      if (!activeCompany) throw new Error('Company not selected');
      // Default to '/' when currentPath is empty (root level folder creation)
      const locationPath = currentPath || '/';
      return apiClient.postForm<CreateFolderResponse>('api/Storage/CreateFolder', {
        companyId: activeCompany.Id,
        newFolderName: folderName,
        currentLocationPath: locationPath,
      });
    },
    onSuccess: async (response) => {
      if (!response?.IsSuccess) {
        setError(response?.ErrorMessage || 'Failed to create folder');
      } else {
        addSnackbar('Folder created successfully', 'success');
        handleClose();
        queryClient.invalidateQueries({ queryKey: ['gridData'] });
        // Invalidate the specific grid data query for current company and path
        if (activeCompany) {
          queryClient.invalidateQueries({
            queryKey: ['gridData', activeCompany.Id, currentPath],
          });
        }
      }
    },
    onError: (error: unknown) => {
      let message = 'Unknown error';
      if (error instanceof Error) message = error.message;
      addSnackbar('Folder creation failed', 'error', message, true);
    },
  });

  const handleClose = () => {
    if (isControlled) {
      onOpenChange?.(false);
    } else {
      setInternalOpen(false);
    }
    setFolderName('');
    setError('');
    setWarning('');
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (isControlled) {
      onOpenChange?.(newOpen);
    } else {
      setInternalOpen(newOpen);
    }
    if (!newOpen) {
      setFolderName('');
      setError('');
      setWarning('');
    }
  };

  const handleCreate = () => {
    setError('');

    if (!folderName.trim()) {
      setError('Folder name is required');
      return;
    }

    if (folderName.trim().length === 0) {
      setError('Folder name cannot be empty');
      return;
    }

    createFolderMutation.mutate();
  };

  return (
    <>
      <Button
        color="inherit"
        startIcon={<CreateNewFolder />}
        onClick={() => handleOpenChange(true)}
        sx={{ color: 'white' }}
      >
        Create Folder
      </Button>

      <Dialog
        data-testid="create-folder-dialog"
        open={open}
        onClose={() => handleOpenChange(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { minWidth: '400px' },
        }}
      >
        <DialogTitle data-testid="create-folder-dialog-title">Create Folder</DialogTitle>
        <DialogContent>
          <TextField
            data-testid="create-folder-dialog-input"
            autoFocus
            fullWidth
            margin="dense"
            label="Folder name"
            value={folderName}
            onChange={(e) => {
              setFolderName(e.target.value);
              setError('');

              // Update warning for periods
              if (e.target.value.includes('.')) {
                setWarning("It's recommended not to use period in the folder name");
              } else {
                setWarning('');
              }
            }}
            error={Boolean(error)}
            helperText={error || warning}
            disabled={createFolderMutation.isPending}
            InputProps={{
              endAdornment: warning && !error && (
                <InputAdornment position="end">
                  <Tooltip title={warning}>
                    <WarningIcon sx={{ color: 'warning.main', cursor: 'help' }} />
                  </Tooltip>
                </InputAdornment>
              ),
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            data-testid="create-folder-dialog-cancel-button"
            onClick={() => handleOpenChange(false)}
            disabled={createFolderMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            data-testid="create-folder-dialog-submit-button"
            onClick={handleCreate}
            variant="contained"
            disabled={createFolderMutation.isPending || !folderName.trim()}
            sx={{ minWidth: '100px' }}
          >
            {createFolderMutation.isPending ? 'Creating...' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
